<!-- src/views/treatment-plans/TreatmentPlanDetail.vue -->
<template>
    <div class="treatment-plan-detail">
        <!-- Back button and actions bar -->
        <div class="d-flex justify-content-between align-items-center mb-4">
            <router-link to="/treatment-plans" class="btn btn-outline-primary">
                <i class="bi bi-arrow-left me-2"></i>Volver
            </router-link>

            <div>
                <router-link :to="`/treatment-plans/${planId}/edit`" class="btn btn-primary">
                    <i class="bi bi-pencil me-1"></i> Editar
                </router-link>
            </div>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="d-flex justify-content-center my-5">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Cargando...</span>
            </div>
        </div>

        <!-- Error state -->
        <div v-else-if="error" class="alert alert-danger">
            <i class="bi bi-exclamation-triangle-fill me-2"></i>
            {{ error }}
            <div class="mt-3">
                <router-link to="/treatment-plans" class="btn btn-outline-primary">
                    <i class="bi bi-arrow-left me-2"></i>Volver a planes de tratamiento
                </router-link>
            </div>
        </div>

        <!-- Plan details -->
        <div v-else-if="plan" class="row">
            <div class="col-md-8">
                <div class="card mb-4">
                    <div class="card-header">
                        <h5 class="mb-0">Detalles del Plan de Tratamiento</h5>
                    </div>
                    <div class="card-body">
                        <div class="row mb-3">
                            <div class="col-md-4 fw-bold">Fecha de creación:</div>
                            <div class="col-md-8">{{ formatDate(plan.created_at) }}</div>
                        </div>
                        <div class="row mb-3">
                            <div class="col-md-4 fw-bold">Última actualización:</div>
                            <div class="col-md-8">{{ formatDate(plan.updated_at) }}</div>
                        </div>
                        <div class="row mb-3">
                            <div class="col-md-4 fw-bold">Diagnóstico:</div>
                            <div class="col-md-8">
                                <div v-if="plan.diagnostic" class="text-container">
                                    <pre class="text-content">{{ plan.diagnostic }}</pre>
                                </div>
                                <p v-else class="text-muted">No hay diagnóstico registrado</p>
                            </div>
                        </div>
                        <div class="row mb-3">
                            <div class="col-md-4 fw-bold">Plan de tratamiento:</div>
                            <div class="col-md-8">
                                <div v-if="plan.treatment_plan" class="text-container">
                                    <pre class="text-content">{{ plan.treatment_plan }}</pre>
                                </div>
                                <p v-else class="text-muted">No hay detalles del plan registrados</p>
                            </div>
                        </div>
                        <div class="row mb-3">
                            <div class="col-md-4 fw-bold">Observaciones:</div>
                            <div class="col-md-8">
                                <div v-if="plan.observations" class="text-container">
                                    <pre class="text-content">{{ plan.observations }}</pre>
                                </div>
                                <p v-else class="text-muted">No hay observaciones registradas</p>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Appointments section -->
                <div class="card">
                    <div class="card-header d-flex justify-content-between align-items-center">
                        <h5 class="mb-0">Citas relacionadas</h5>
                        <router-link :to="`/appointments/new?patient=${patient ? patient.id : ''}`"
                            class="btn btn-sm btn-primary">
                            <i class="bi bi-plus-circle me-1"></i> Nueva cita
                        </router-link>
                    </div>
                    <div class="card-body p-0">
                        <div v-if="appointments.length === 0" class="text-center py-4">
                            <p class="text-muted mb-0">No hay citas programadas</p>
                        </div>
                        <ul v-else class="list-group list-group-flush">
                            <li v-for="appointment in appointments" :key="appointment.id" class="list-group-item">
                                <div class="d-flex justify-content-between align-items-center">
                                    <div>
                                        <div>{{ formatDate(appointment.fecha) }} - {{ formatTime(appointment.hora) }}
                                        </div>
                                        <div class="text-muted small">{{ appointment.tipo_tratamiento }}</div>
                                    </div>
                                    <span class="badge rounded-pill"
                                        :class="getAppointmentStatusClass(appointment.estado)">
                                        {{ appointment.estado }}
                                    </span>
                                </div>
                                <div class="mt-2">
                                    <router-link :to="`/appointments/${appointment.id}`"
                                        class="btn btn-sm btn-outline-primary">
                                        Ver detalle
                                    </router-link>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>

            <div class="col-md-4">
                <div class="card mb-4">
                    <div class="card-header">
                        <h5 class="mb-0">Paciente</h5>
                    </div>
                    <div class="card-body">
                        <div v-if="patient" class="d-flex align-items-center mb-3">
                            <div class="avatar-placeholder me-3">
                                {{ getInitials(patient.first_name, patient.last_name) }}
                            </div>
                            <div>
                                <h5 class="mb-0">{{ patient.first_name }} {{ patient.last_name }}</h5>
                                <p v-if="patient.fecha_nacimiento" class="text-muted mb-0">
                                    {{ patient.age }} años
                                </p>
                            </div>
                        </div>

                        <div v-if="patient" class="patient-details">
                            <div class="mb-2" v-if="patient.phone_number">
                                <i class="bi bi-telephone me-2"></i>
                                {{ patient.phone_number }}
                            </div>
                            <div class="mb-2" v-if="patient.email">
                                <i class="bi bi-envelope me-2"></i>
                                {{ patient.email }}
                            </div>
                            <div class="mb-2">
                                <i class="bi bi-clipboard-pulse me-2"></i>
                                <span class="badge" :class="getStatusClass(patient.treatment_status)">
                                    {{ getTreatmentStatusName(patient.treatment_status) }}
                                </span>
                            </div>
                        </div>

                        <div class="mt-3">
                            <router-link :to="`/patients/${patient.id}`" class="btn btn-sm btn-outline-primary w-100">
                                <i class="bi bi-person me-1"></i> Ver perfil del paciente
                            </router-link>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { patientService, appointmentService, treatmentPlanService } from '@/services/api';

export default {
    name: 'TreatmentPlanDetail',
    props: {
        id: {
            type: [String, Number],
            required: true
        }
    },
    data() {
        return {
            planId: null,
            plan: null,
            patient: null,
            appointments: [],
            loading: true,
            error: null
        };
    },
    created() {
        this.planId = this.id;
        this.fetchData();
    },
    methods: {
        async fetchData() {
            this.loading = true;
            this.error = null;

            try {
                // Fetch the treatment plan directly
                const planResponse = await treatmentPlanService.get(this.planId);
                this.plan = planResponse.data;

                if (!this.plan) {
                    this.error = 'Plan de tratamiento no encontrado';
                    return;
                }

                // Fetch the patient
                const patientResponse = await patientService.get(this.plan.patient);
                this.patient = patientResponse.data;

                // Fetch appointments for this patient
                await this.fetchAppointments();
            } catch (error) {
                console.error('Error fetching treatment plan details:', error);
                this.error = 'No se pudo cargar el plan de tratamiento. Por favor, intenta de nuevo.';
            } finally {
                this.loading = false;
            }
        },

        async fetchAppointments() {
            try {
                if (!this.patient) return;

                // Get appointments for this patient
                const appointmentsResponse = await appointmentService.getAll();
                this.appointments = appointmentsResponse.data
                    .filter(app => app.patient === this.patient.id)
                    .sort((a, b) => new Date(b.fecha) - new Date(a.fecha));
            } catch (error) {
                console.error('Error fetching appointments:', error);
            }
        },

        formatDate(dateString) {
            if (!dateString) return '';

            // Create a date object from the input string
            const date = new Date(dateString);

            // Add one day to compensate for timezone issues
            date.setDate(date.getDate() + 1);

            // Format using locale
            return date.toLocaleDateString('es-ES');
        },

        formatTime(timeString) {
            if (!timeString) return '';
            return timeString.substring(0, 5); // Format "HH:MM" from "HH:MM:SS"
        },

        getInitials(firstName, lastName) {
            const first = firstName ? firstName.charAt(0).toUpperCase() : '';
            const last = lastName ? lastName.charAt(0).toUpperCase() : '';
            return `${first}${last}`;
        },

        getTreatmentStatusName(status) {
            const statuses = {
                'en_planificacion': 'En Planificación',
                'iniciado': 'Iniciado',
                'completado': 'Terminado',
                'en_pausa': 'En Pausa'
            };

            return statuses[status] || status;
        },

        getStatusClass(status) {
            const statusClasses = {
                'en_planificacion': 'bg-info',
                'iniciado': 'bg-primary',
                'completado': 'bg-success',
                'en_pausa': 'bg-warning'
            };

            return statusClasses[status] || 'bg-secondary';
        },

        getAppointmentStatusClass(status) {
            const statusClasses = {
                'programada': 'bg-info',
                'completada': 'bg-success',
                'inasistencia': 'bg-danger',
                'confirmada': 'bg-warning'
            };

            return statusClasses[status] || 'bg-secondary';
        }
    }
};
</script>

<style scoped>
.avatar-placeholder {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: #e9ecef;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1rem;
    font-weight: bold;
    color: #495057;
}

.text-container {
    background-color: #f8f9fa;
    border-radius: 0.375rem;
    padding: 0;
    overflow: hidden;
}

.text-content {
    font-family: inherit;
    margin: 0;
    padding: 0.75rem;
    background-color: transparent;
    border: none;
    font-size: 0.95rem;
    line-height: 1.5;
    white-space: pre-line;
    color: #212529;
    overflow-wrap: break-word;
    width: 100%;
}
</style>